import type {
  CreateTicketDTO,
  FetchTicketResponsePriority,
  CreateAssignmentDTO,
  CreateActionLabourDTO,
  FetchAllTickets as FetchAllTicketsDTO,
  CreateAssignmentDTOStatus,
  FetchAllAssignments,
  FetchAssignmentResponse,
  TicketFiltersDTO,
  AddArticlesToTicketType,
} from "src/api/models";
import { CreateAssignmentDTOType } from "src/api/models";
import {
  getFetchTicket,
  postCreateTicket,
  postAddArticleToTicket,
  postUpdateTicketAssignee,
  postUpdateTicketPriority,
  postFetchAllTickets,
  postUpdateTicketTitle,
  postUpdateTicketDescription,
  postFetchOverdueScheduledTickets,
  postAddUnitToTicket,
  postAddSiteToTicket,
  postFetchAllStatuses,
  postFetchAllUnscheduledTickets,
  usePostFetchAllTickets,
  postCreateAssignment,
  postCreateActionLabour,
  postCloseAssignment,
  postFetchAllAssignments,
  getFetchAssignment,
  postUpdateTicketExpectedDuration,
  postUpdateActionLabourTotalHours,
  deleteRemoveArticleAssociationFromTicket,
  postPinTicket,
  postUnpinTicket,
  postUpdateSiteContactPerson,
  patchUpdateTicketOrganisation,
  postOpenAssignment,
  postScheduleDate,
  deleteRemoveAssignment,
  postUpdateArticleAssociationQuantity,
  postUpdateArticleAssociationPrice,
  postUpdateOrganisationContactPerson,
  postUpdateTicketStatus,
} from "src/api/queries/order-portal-bff";
import { useNotification } from "src/composables/useNotification";
import { ref } from "vue";

interface LaborEntry {
  assignee_id: string;
  labor_hours: number;
  labor_type: string;
}

interface AssignmentData {
  action_id: string;
  assignee_id?: string;
  laborEntries?: LaborEntry[];
  type?: CreateAssignmentDTOType;
}

export function useTicketService() {
  const { showSuccess, showError } = useNotification();

  async function fetchTicket(ticketId: string) {
    try {
      const response = await getFetchTicket(ticketId);
      return response;
    } catch (error) {
      showError();
      throw error;
    }
  }

  async function fetchTickets(params: TicketFiltersDTO) {
    try {
      const response = await postFetchAllTickets(params, { limit: 100 });
      return response;
    } catch (error) {
      showError();
      throw error;
    }
  }

  async function fetchOverdueScheduledTickets() {
    try {
      const response = await postFetchOverdueScheduledTickets();
      return response;
    } catch (error) {
      showError();
      throw error;
    }
  }

  async function fetchUnscheduledTickets() {
    try {
      const response = await postFetchAllUnscheduledTickets();
      return response;
    } catch (error) {
      showError();
      throw error;
    }
  }

  async function fetchAllTicketStatuses() {
    try {
      const response = await postFetchAllStatuses();
      const result = Array.isArray(response) ? response : [response];
      return result;
    } catch (error) {
      showError();
      throw error;
    }
  }

  async function fetchAllTicketAssignments(ticketId: string): Promise<FetchAssignmentResponse[]> {
    try {
      const response = await postFetchAllAssignments({ ticket_id: ticketId });
      return response.data || [];
    } catch (error) {
      showError();
      throw error;
    }
  }
  async function updateTicketPriority(ticketId: string, priority: FetchTicketResponsePriority) {
    try {
      const response = await postUpdateTicketPriority(ticketId, {
        priority: priority as any,
      });

      return response;
    } catch (error) {
      showError({ message: "Failed to update priority" });
      throw error;
    }
  }

  async function updateTicketStatus(ticketId: string, statusId: string, statusDetailId?: string | null) {
    try {
      const response = await postUpdateTicketStatus(ticketId, {
        status_id: statusId,
        status_detail_id: statusDetailId,
      });
      showSuccess({ message: "Status updated successfully" });
      return response;
    } catch (error) {
      showError({ message: "Failed to update status" });
      throw error;
    }
  }

  async function updateTicketAssignee(ticketId: string, assigneeId: string | null) {
    try {
      const response = await postUpdateTicketAssignee(ticketId, {
        assignee_id: assigneeId,
      } as any);

      return response;
    } catch (error) {
      showError();
      throw error;
    }
  }

  async function updateTicketPlannedDate(ticketId: string, plannedDate: string) {
    try {
      const response = await postScheduleDate(ticketId, {
        scheduled_date: plannedDate,
      });

      return response;
    } catch (error) {
      throw error;
    }
  }

  async function updateTicketTitle(ticketId: string, title: string) {
    try {
      const response = await postUpdateTicketTitle(ticketId, {
        title,
      });

      return response;
    } catch (error) {
      showError();
      throw error;
    }
  }

  async function updateTicketDescription(ticketId: string, description: string) {
    try {
      const response = await postUpdateTicketDescription(ticketId, {
        description,
      });

      return response;
    } catch (error) {
      showError();
      throw error;
    }
  }

  async function updateTicketUnit(ticketId: string, unitId: string) {
    try {
      const response = await postAddUnitToTicket(ticketId, unitId);

      return response;
    } catch (error) {
      showError();
      throw error;
    }
  }

  async function updateTicketSite(ticketId: string, siteId: string) {
    try {
      const response = await postAddSiteToTicket(ticketId, siteId);
      return response;
    } catch (error) {
      showError();
      throw error;
    }
  }

  async function updateTicketOrganisation(ticketId: string, organisationId: string | undefined) {
    try {
      const response = await patchUpdateTicketOrganisation(ticketId, {
        organisation_id: organisationId,
      });
      return response;
    } catch (error) {
      showError();
      throw error;
    }
  }

  async function updateTicketOrganisationContactPerson(ticketId: string, organisationContactPersonId: string | undefined) {
    try {
      const response = await postUpdateOrganisationContactPerson(ticketId, {
        organisation_contact_person_id: organisationContactPersonId || undefined,
      });
      showSuccess({ message: "Organisation contact person updated successfully" });
      return response;
    } catch (error) {
      showError({ message: "Failed to update organisation contact person" });
      throw error;
    }
  }

  async function updateTicketSiteContactPerson(ticketId: string, siteContactPersonId: string | undefined) {
    console.log("updateTicketSiteContactPerson", ticketId, siteContactPersonId);
    // try {
    //   const response = await putAddSiteContactPersonToTicket(ticketId, siteContactPersonId);
    //   return response;
    // } catch (error) {
    //   showError();
    //   throw error;
    // }
  }

  interface ArticleToAdd {
    article_id: string;
    quantity?: number;
    type?: AddArticlesToTicketType;
    price?: number | null;
  }

  async function addArticlesToTicket(
    ticketId: string,
    articles: string[] | ArticleToAdd[],
    quantity = 1
  ) {
    try {
      // Support both simple array of IDs and detailed article objects
      const articlesPayload = Array.isArray(articles) && typeof articles[0] === 'string'
        ? (articles as string[]).map(id => ({
          article_id: id,
          quantity
        }))
        : (articles as ArticleToAdd[]).map(article => ({
          article_id: article.article_id,
          quantity: article.quantity ?? quantity,
          type: article.type,
          price: article.price,
        }));

      const response = await postAddArticleToTicket(ticketId, {
        articles: articlesPayload,
      });

      return response;
    } catch (error) {
      showError();
      throw error;
    }
  }

  async function updateArticleQuantity(associationId: string, quantity: number) {
    try {
      const response = await postUpdateArticleAssociationQuantity(associationId, { quantity });
      showSuccess({ message: "Article quantity updated successfully" });
      return response;
    } catch (error) {
      showError({ message: "Failed to update article quantity" });
      throw error;
    }
  }

  async function updateArticlePrice(associationId: string, price: number) {
    try {
      const response = await postUpdateArticleAssociationPrice(associationId, { price });
      showSuccess({ message: "Article price updated successfully" });
      return response;
    } catch (error) {
      showError({ message: "Failed to update article price" });
      throw error;
    }
  }

  async function removeArticleFromTicket(ticketId: string, associationId: string) {
    try {
      const response = await deleteRemoveArticleAssociationFromTicket(ticketId, associationId);
      return response;
    } catch (error) {
      showError({ message: "Failed to remove article from ticket" });
      throw error;
    }
  }

  async function addAssignmentToTicket(ticketId: string, assignmentData: AssignmentData) {
    try {
      const assignmentPayload: CreateAssignmentDTO = {
        action_id: assignmentData.action_id,
        ticket_id: ticketId,
        type: assignmentData.type ?? CreateAssignmentDTOType.variable,
      };

      const newAssignmentId = await postCreateAssignment(assignmentPayload);
      assignmentData.laborEntries = assignmentData.laborEntries ?? [];
      if (!newAssignmentId) {
        showError({ message: "Failed to create assignment" });
        return null;
      }


      if (assignmentData.laborEntries?.length === 0) {
        showSuccess({ message: "Assignment added but no labor hours were specified" });
        return newAssignmentId;
      }

      const results = await Promise.allSettled(
        assignmentData.laborEntries.map(entry => {
          const laborPayload: CreateActionLabourDTO = {
            total_hours: entry.labor_hours,
            type: entry.labor_type as any,
            assignment_id: newAssignmentId,
            assignee_id: entry.assignee_id
          };

          return postCreateActionLabour(laborPayload);
        })
      );

      const failedCount = results.filter(result => result.status === 'rejected').length;

      if (failedCount === 0) {
        showSuccess({ message: "Assignment added successfully" });
      } else if (failedCount < results.length) {
        showError({ message: `Assignment added but ${failedCount} labor entries failed` });
      } else {
        showError({ message: "Assignment added but all labor entries failed" });
      }

      return newAssignmentId;
    } catch (error) {
      showError({ message: "Failed to add assignment" });
      throw error;
    }
  }

  async function addLaborToAssignment(assignmentId: string, laborEntries: LaborEntry[]) {
    try {
      if (!assignmentId) {
        showError({ message: "Invalid assignment ID" });
        return false;
      }

      if (laborEntries.length === 0) {
        showError({ message: "No labor entries provided" });
        return false;
      }

      const results = await Promise.allSettled(
        laborEntries.map(entry => {
          const laborPayload: CreateActionLabourDTO = {
            total_hours: entry.labor_hours,
            type: entry.labor_type as any,
            assignment_id: assignmentId,
            assignee_id: entry.assignee_id
          };

          return postCreateActionLabour(laborPayload);
        })
      );

      const failedCount = results.filter(result => result.status === 'rejected').length;

      if (failedCount === 0) {
        showSuccess({ message: "Labor hours added successfully" });
        return true;
      } else if (failedCount < results.length) {
        showError({ message: `${results.length - failedCount} labor entries added but ${failedCount} failed` });
        return true;
      } else {
        showError({ message: "Failed to add any labor entries" });
        return false;
      }
    } catch (error) {
      showError({ message: "Failed to add labor hours" });
      throw error;
    }
  }

  async function closeAssignment(assignmentId: string) {
    try {
      await postCloseAssignment(assignmentId);
      // Since the API doesn't seem to return anything, we assume success if no error is thrown
      showSuccess({ message: "Assignment closed successfully" });
      return true;
    } catch (error) {
      showError({ message: "Failed to close assignment" });
      throw error;
    }
  }

  async function openAssignment(assignmentId: string) {
    try {
      await postOpenAssignment(assignmentId);
      showSuccess({ message: "Assignment opened successfully" });
      return true;
    } catch (error) {
      showError({ message: "Failed to open assignment" });
      throw error;
    }
  }

  async function getAssignment(assignmentId: string): Promise<FetchAssignmentResponse> {
    try {
      const response = await getFetchAssignment(assignmentId);
      return response;
    } catch (error) {
      showError({ message: "Failed to get assignment" });
      throw error;
    }
  }

  async function createTicket(ticket: CreateTicketDTO) {
    try {
      const response = await postCreateTicket(ticket);
      return response;
    } catch (error) {
      showError();
      throw error;
    }
  }

  async function updateTicketUnitSite(ticketId: string, siteId: string) {
    try {
      await postAddSiteToTicket(ticketId, siteId);
      showSuccess({ message: "Unit site updated successfully" });
    } catch (error) {
      showError({ message: "Failed to update unit site" });
      throw error;
    }
  }

  async function updateTicketContactSite(ticketId: string, siteId?: string) {
    try {
      await postUpdateSiteContactPerson(ticketId, {
        site_contact_person_id: siteId || undefined,
      });
      showSuccess({ message: "Contact site updated successfully" });
    } catch (error) {
      showError({ message: "Failed to update contact site" });
      throw error;
    }
  }

  async function updateTicketExpectedDuration(ticketId: string, expectedDuration: { hours: number, minutes: number }) {
    try {
      await postUpdateTicketExpectedDuration(ticketId, {
        expected_duration: {
          hours: expectedDuration.hours,
          minutes: expectedDuration.minutes
        }
      });
      showSuccess({ message: "Expected duration updated successfully" });
      return { success: true };
    } catch (error) {
      showError({ message: "Failed to update expected duration" });
      throw error;
    }
  }

  async function updateActionLabourTotalHours(assignmentId: string, totalHours: number) {
    try {
      await postUpdateActionLabourTotalHours(assignmentId, { total_hours: totalHours });
      showSuccess({ message: "Labor hours updated successfully" });
    } catch (error) {
      showError({ message: "Failed to update labor hours" });
      throw error;
    }
  }

  async function removeActionLabour(actionLabourId: string): Promise<boolean> {
    try {
      // await postDeleteActionLabour(actionLabourId);
      showSuccess({ message: "Labor entry removed successfully" });
      return true;
    } catch (error) {
      showError({ message: "Failed to remove labor entry" });
      throw error;
    }
  }

  async function updateTicketPinned(ticketId: string, pinned: boolean) {
    try {
      if (pinned) {
        await postPinTicket(ticketId);
      } else {
        await postUnpinTicket(ticketId);
      }
    } catch (error) {
      showError({ message: "Failed to update ticket pinned" });
      throw error;
    }
  }

  async function deleteAssignment(assignmentId: string): Promise<boolean> {
    try {
      await deleteRemoveAssignment(assignmentId);
      showSuccess({ message: "Assignment deleted successfully" });
      return true;
    } catch (error) {
      showError({ message: "Failed to delete assignment" });
      throw error;
    }
  }


  return {
    updateTicketPriority,
    updateTicketStatus,
    updateTicketAssignee,
    updateTicketPlannedDate,
    updateTicketTitle,
    updateTicketDescription,
    updateTicketUnit,
    updateTicketSite,
    updateTicketOrganisation,
    updateTicketOrganisationContactPerson,
    updateTicketSiteContactPerson,
    addArticlesToTicket,
    updateArticleQuantity,
    updateArticlePrice,
    removeArticleFromTicket,
    addAssignmentToTicket,
    addLaborToAssignment,
    updateActionLabourTotalHours,
    closeAssignment,
    openAssignment,
    deleteAssignment,
    createTicket,
    fetchTicket,
    fetchTickets,
    fetchOverdueScheduledTickets,
    fetchAllTicketStatuses,
    fetchUnscheduledTickets,
    updateTicketUnitSite,
    updateTicketContactSite,
    fetchAllTicketAssignments,
    getAssignment,
    updateTicketExpectedDuration,
    removeActionLabour,
    updateTicketPinned,
  };
}
